import React, { useState, useRef, useEffect, useCallback } from 'react';

/**
 * ScrollableTabs - Horizontal scrollable tab container with gradient indicators
 * Shows gradient overlays to indicate more content in scroll direction
 */
const ScrollableTabs = ({ children, className = '' }) => {
  const scrollRef = useRef(null);
  const [showLeftGradient, setShowLeftGradient] = useState(false);
  const [showRightGradient, setShowRightGradient] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;
    const isScrollable = scrollWidth > clientWidth;
    
    // Show left gradient if scrolled away from start
    setShowLeftGradient(isScrollable && scrollLeft > 5);
    
    // Show right gradient if not at the end
    setShowRightGradient(isScrollable && scrollLeft < scrollWidth - clientWidth - 5);
  }, []);

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [checkScroll]);

  // Re-check when children change
  useEffect(() => {
    checkScroll();
  }, [children, checkScroll]);

  return (
    <div className={`relative ${className}`}>
      {/* Left gradient indicator */}
      {showLeftGradient && (
        <div 
          className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-cream to-transparent z-10 pointer-events-none"
          aria-hidden="true"
        />
      )}
      
      {/* Scrollable container */}
      <div
        ref={scrollRef}
        onScroll={checkScroll}
        className="flex gap-1 overflow-x-auto scrollbar-hide scroll-smooth"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {children}
      </div>
      
      {/* Right gradient indicator */}
      {showRightGradient && (
        <div 
          className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-cream to-transparent z-10 pointer-events-none"
          aria-hidden="true"
        />
      )}
    </div>
  );
};

export default ScrollableTabs;
